import { Button, Col, Form, Input, Modal, Row, Switch, Card, Typography, Space, Divider, Alert } from "antd";
import { getInstitutionClients, IInstitution, InstitutionClient, updateInstitution, updateInstitutionClients } from "../../redux/reducer/institution";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useCallback, useEffect, useState, CSSProperties } from "react";
import { useMsal } from "@azure/msal-react";
import ClientModal from "./ClientModal";
import {
    UserOutlined,
    LoginOutlined,
    TeamOutlined,
    GlobalOutlined,
    InfoCircleOutlined,
    SaveOutlined,
    UndoOutlined,
    MinusOutlined,
    PlusOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '24px',
    },
    section: {
        marginBottom: '32px',
    },
    sectionTitle: {
        marginBottom: '16px',
        fontSize: '18px',
        fontWeight: 600,
    },
    formItem: {
        marginBottom: '24px',
    },
    helpText: {
        color: '#666',
        fontSize: '14px',
        marginTop: '8px',
    },
    actionButtons: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid #f0f0f0',
    },
};

interface SettingsConfigurationParams {
    institution: IInstitution;
}

const SettingsConfiguration = ({
    institution,
}: SettingsConfigurationParams) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm<Partial<IInstitution>>();
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION'])
    const [cidRequired, setCidRequired] = useState<boolean>(institution?.cidRequired ?? false);
    const [last4Required, setLast4Required] = useState<boolean>(institution?.requireLast4Tin ?? false);
    const [cidModal, setCidModal] = useState<boolean>(false);
    const [minimizedSections, setMinimizedSections] = useState<string[]>([]);

    const toggleMinimize = (sectionKey: string) => {
        setMinimizedSections(prev =>
            prev.includes(sectionKey)
                ? prev.filter(key => key !== sectionKey)
                : [...prev, sectionKey]
        );
    };

    const onFinish = useCallback((values: Partial<IInstitution>) => {
        dispatch(updateInstitution({
            input: {
                institutionId: institution.institutionId,
                id: institution.id,
                ...values,
            },
            message: { success: 'Institution updated!' },
            headers: {
                Authorization: `Bearer ${accounts[0].idToken}`,
            }
        }))
    }, [institution, accounts, dispatch]);

    useEffect(() => {
        dispatch(getInstitutionClients({
            input: { institutionId: institution.institutionId },
            headers: { Authorization: `Bearer ${accounts[0].idToken}` },
        }))
    }, [institution]);

    return (
        <div style={styles.container}>
            <Modal
                open={cidModal}
                onCancel={() => { setCidModal(false); }}
                destroyOnHidden
                footer={null}
                maskClosable={false}
                keyboard={false}
                width='80%'
                title={
                    <Space>
                        <TeamOutlined />
                        <span>Manage Clients</span>
                    </Space>
                }
            >
                <ClientModal
                    institution={institution}
                    onSubmit={(values: { clients: InstitutionClient[]; }) => {
                        dispatch(updateInstitutionClients({
                            input: {
                                clients: values.clients,
                                institutionId: institution.institutionId,
                                id: institution.id,
                            },
                            headers: { Authorization: `Bearer ${accounts[0].idToken}` },
                            onSuccess: () => { setCidModal(false); }
                        }))
                    }}
                    onCancel={() => { setCidModal(false); }}
                />
            </Modal>

            <Form
                form={form}
                initialValues={institution}
                onFinish={onFinish}
                layout="vertical"
            >
                {/* Authentication Section */}
                <div style={styles.section}>
                    <Card
                        title={
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <LoginOutlined /> Authentication Settings
                                    </Title>
                                </Col>
                                <Col>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={minimizedSections.includes('auth') ? <PlusOutlined /> : <MinusOutlined />}
                                        onClick={() => toggleMinimize('auth')}
                                        title={minimizedSections.includes('auth') ? "Expand" : "Minimize"}
                                    />
                                </Col>
                            </Row>
                        }
                        styles={{ body: minimizedSections.includes('auth') ? { display: 'none' } : {} }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Row align="middle" gutter={16}>
                                <Col>
                                    <Form.Item
                                        name='allowLogin'
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col flex="auto">
                                    <div>
                                        <Text strong>Allow User Login</Text>
                                        <div style={styles.helpText}>
                                            Enable users to log in with email-based magic links
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Divider style={{ margin: '16px 0' }} />

                            <Row align="middle" gutter={16}>
                                <Col>
                                    <Form.Item
                                        name='anonymousCreate'
                                        valuePropName="checked"
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col flex="auto">
                                    <div>
                                        <Text strong>Allow Anonymous Applications</Text>
                                        <div style={styles.helpText}>
                                            Let users create applications without logging in first
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Space>
                    </Card>
                </div>

                {/* Client Management Section */}
                <div style={styles.section}>
                    <Card
                        title={
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <UserOutlined /> Client Management
                                    </Title>
                                </Col>
                                <Col>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={minimizedSections.includes('client') ? <PlusOutlined /> : <MinusOutlined />}
                                        onClick={() => toggleMinimize('client')}
                                        title={minimizedSections.includes('client') ? "Expand" : "Minimize"}
                                    />
                                </Col>
                            </Row>
                        }
                        styles={{ body: minimizedSections.includes('client') ? { display: 'none' } : {} }}
                    >
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Row align="middle" gutter={16}>
                                <Col>
                                    <Form.Item
                                        name='cidRequired'
                                        valuePropName="checked"
                                        getValueFromEvent={(e) => { setCidRequired(e); return e; }}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col flex="auto">
                                    <div>
                                        <Text strong>Require Client ID</Text>
                                        <div style={styles.helpText}>
                                            Users must provide a valid client ID to access the application
                                        </div>
                                    </div>
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        ghost
                                        icon={<TeamOutlined />}
                                        disabled={!cidRequired}
                                        onClick={() => { setCidModal(true); }}
                                    >
                                        Manage Clients
                                    </Button>
                                </Col>
                            </Row>
                            {cidRequired && (
                                <Alert
                                    message="Client ID Required"
                                    description="When enabled, users must provide a valid client ID that matches one of the configured clients to access the application."
                                    type="info"
                                    showIcon
                                    closable
                                    icon={<InfoCircleOutlined />}
                                />
                            )}

                            <Divider style={{ margin: '16px 0' }} />

                            <Row align="middle" gutter={16}>
                                <Col>
                                    <Form.Item
                                        name='requireLast4Tin'
                                        valuePropName="checked"
                                        getValueFromEvent={(e) => { setLast4Required(e); return e; }}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col flex="auto">
                                    <div>
                                        <Text strong>Require Last 4 of TIN</Text>
                                        <div style={styles.helpText}>
                                            Users must provide the last 4 digits of their TIN to proceed
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            { last4Required && (
                                <Alert
                                    message="Last 4 of TIN Required"
                                    description="When enabled, users must provide the last 4 of the TIN to access the application."
                                    type="info"
                                    showIcon
                                    closable
                                    icon={<InfoCircleOutlined />}
                                />
                            )}

                        </Space>
                    </Card>
                </div>

                {/* System Information */}
                <div style={styles.section}>
                    <Card
                        title={
                            <Row justify="space-between" align="middle">
                                <Col>
                                    <Title level={4} style={{ margin: 0 }}>
                                        <GlobalOutlined /> System Information
                                    </Title>
                                </Col>
                                <Col>
                                    <Button
                                        type="text"
                                        size="small"
                                        icon={minimizedSections.includes('system') ? <PlusOutlined /> : <MinusOutlined />}
                                        onClick={() => toggleMinimize('system')}
                                        title={minimizedSections.includes('system') ? "Expand" : "Minimize"}
                                    />
                                </Col>
                            </Row>
                        }
                        styles={{ body: minimizedSections.includes('system') ? { display: 'none' } : {} }}
                    >
                        <Form.Item
                            name='host'
                            label={<Text strong>Application Host URL</Text>}
                            tooltip="The base URL where this institution's application is hosted"
                        >
                            <Input
                                disabled
                                prefix={<GlobalOutlined />}
                                style={{ fontFamily: 'monospace' }}
                            />
                        </Form.Item>
                    </Card>
                </div>

                {/* Action Buttons */}
                <div style={styles.actionButtons}>
                    <Row justify="end" gutter={16}>
                        <Col>
                            <Button
                                size="large"
                                icon={<UndoOutlined />}
                                loading={loading}
                                onClick={() => { form.resetFields(); }}
                            >
                                Reset Changes
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                size="large"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={loading}
                                htmlType="submit"
                            >
                                Save Settings
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Form>
        </div>
    );
};

export default SettingsConfiguration;
